import {Action, Selector, State, StateContext} from '@ngxs/store';
import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {tap} from 'rxjs/operators';
import {Products} from '../actions/products.actions';
import {ProductsService} from '../services/products.service';
import {Product} from '../models/products/product.model';
import {JobProduct} from '../models/products/jobProduct.model';
import {Auth} from '../actions/auth.actions';
import ClearState = Auth.ClearState;
import GetProducts = Products.GetProducts;
import GetProduct = Products.GetProduct;
import CreateProduct = Products.CreateProduct;
import UpdateProduct = Products.UpdateProduct;
import DeleteProduct = Products.DeleteProduct;
import UpdateJobProductsLocal = Products.UpdateJobProductsLocal;
import UpdateJobProducts = Products.UpdateJobProducts;
import GetJobProducts = Products.GetJobProducts;

export interface ProductsStateModel {
  products: Array<Product>;
  currentProduct: Product;
  jobProducts: Array<JobProduct>;
}

@State<ProductsStateModel>({
  name: 'products',
  defaults: {
    products: [],
    currentProduct: null,
    jobProducts: [],
  }
})
@Injectable()
export class ProductsState {
  constructor(private productsService: ProductsService
  ) {
  }

  @Selector()
  static products(state: ProductsStateModel): Array<Product> {
    return state.products;
  }

  @Selector()
  static product(state: ProductsStateModel): Product {
    return state.currentProduct;
  }

  @Selector()
  static productsByCategory(state: ProductsStateModel): (category: string) => Array<Product> {
    return (category: string) => {
      return state.products.filter((product) => product.categories.includes(category));
    };
  }

  @Selector()
  static jobProducts(state: ProductsStateModel): Array<JobProduct> {
    return state.jobProducts;
  }

  @Action(GetProducts)
  getProducts(ctx: StateContext<ProductsStateModel>, action: GetProducts): Observable<Product[]> {
    if (!action.force) {
      const products = ctx.getState().products;
      if (products.length > 0) {
        return of(products);
      }
    }
    return this.productsService.getProducts().pipe(
      tap(products => {
        ctx.patchState({
          products
        });
      })
    );
  }

  @Action(GetProduct)
  getProduct(ctx: StateContext<ProductsStateModel>, action: GetProduct): Observable<Product> {
    if (!action.force) {
      const product = ctx.getState().currentProduct;
      if (product) {
        return of(product);
      }

      const cachedProduct = ctx.getState().products.find(p => p.id === action.id);
      if (cachedProduct) {
        ctx.patchState({
          currentProduct: cachedProduct
        });
        return of(cachedProduct);
      }
    }
    return this.productsService.getProduct(action.id).pipe(
      tap(product => {
        ctx.patchState({
          currentProduct: product
        });
      })
    );
  }

  @Action(CreateProduct)
  createProduct(ctx: StateContext<ProductsStateModel>, action: CreateProduct): Observable<Product> {
    return this.productsService.createProduct(action.product, action.images).pipe(
      tap(product => {
        ctx.patchState({
          currentProduct: product
        });
      })
    );
  }

  @Action(UpdateProduct)
  updateProduct(ctx: StateContext<ProductsStateModel>, action: UpdateProduct): Observable<Product> {
    return this.productsService.updateProduct(action.product, action.images).pipe(
      tap(product => {
        ctx.patchState({
          currentProduct: product
        });
      })
    );
  }

  @Action(DeleteProduct)
  deleteProduct(ctx: StateContext<ProductsStateModel>, action: DeleteProduct): Observable<Product> {
    const products = ctx.getState().products;
    const index = products.indexOf(action.product);
    return this.productsService.deleteProduct(action.product).pipe(
      tap(product => {
        ctx.patchState({
          currentProduct: null,
          products: [
            ...products.slice(0, index),
            ...index === products.length - 1 ? [] : products.slice(index + 1)
          ]
        });
      })
    );
  }

  @Action(GetJobProducts)
  getJobProducts(ctx: StateContext<ProductsStateModel>, action: GetJobProducts): Observable<Array<JobProduct>> {
    return this.productsService.getJobProducts(action.id).pipe(
      tap(jobProducts => {
        ctx.patchState({
          jobProducts
        });
      })
    );
  }

  @Action(UpdateJobProductsLocal)
  updateJobProductsLocal(ctx: StateContext<ProductsStateModel>, action: UpdateJobProductsLocal): void {
    ctx.patchState({
      jobProducts: action.products,
    });
  }

  @Action(UpdateJobProducts)
  updateJobProducts(ctx: StateContext<ProductsStateModel>, action: UpdateJobProducts): Observable<Array<JobProduct>> {
    return this.productsService.updateJobProducts(action.jobId, action.products).pipe(
      tap(jobProducts => {
        ctx.patchState({
          jobProducts
        });
      })
    );
  }

  @Action(ClearState)
  clearState(ctx: StateContext<ProductsStateModel>): void {
    ctx.setState({
      products: [],
      currentProduct: null,
      jobProducts: [],
    });
  }
}
